
import React from 'react';

interface ReceiptData {
  receiptNumber: string;
  date: string;
  amount: number;
  currency: string;
  description?: string;
  customerName?: string;
  customerEmail?: string;
  customerPhone?: string;
  customerAddress?: {
    street?: string;
    city?: string;
    postcode?: string;
    country?: string;
  };
  businessProfile?: any;
  includeAddress?: boolean;
}

interface ReceiptPreviewProps {
  receiptData: ReceiptData;
}

const ReceiptPreview = ({ receiptData }: ReceiptPreviewProps) => {
  const {
    receiptNumber,
    date,
    amount,
    currency,
    description,
    customerName,
    customerEmail,
    customerPhone,
    customerAddress,
    businessProfile,
    includeAddress,
  } = receiptData;

  const businessName = businessProfile?.business_name || 'Business';
  const bank = businessProfile?.bank_details;

  const bankFields: { label: string; value?: string }[] = [
    { label: 'Account Name', value: bank?.account_name },
    { label: 'Bank', value: bank?.bank_name },
    { label: 'Sort Code', value: bank?.sort_code },
    { label: 'Account Number', value: bank?.account_number },
    { label: 'IBAN', value: bank?.iban },
    { label: 'SWIFT/BIC', value: bank?.swift },
  ].filter((f) => f.value);

  const hasContact =
    customerName ||
    customerEmail ||
    customerPhone ||
    (includeAddress &&
      customerAddress &&
      (customerAddress.street ||
        customerAddress.city ||
        customerAddress.postcode ||
        customerAddress.country));

  const officeLine = [
    businessName && `Registered Office: ${businessName}`,
    businessProfile?.address,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="max-w-3xl mx-auto bg-white text-[#333] font-sans shadow-sm border border-gray-100">
      <div className="px-12 pt-10 pb-8 flex flex-col min-h-[1000px]">
        {/* Header */}
        <div className="flex justify-end">
          {businessProfile?.company_logo_url ? (
            <img
              src={businessProfile.company_logo_url}
              alt={businessName}
              className="h-12 w-auto object-contain"
            />
          ) : (
            <h2 className="text-xl font-bold text-black tracking-tight">{businessName}</h2>
          )}
        </div>

        <div className="grid grid-cols-2 gap-8 mt-10">
          <div>
            <h1 className="text-3xl font-normal text-black tracking-tight">RECEIPT</h1>
            {customerName && (
              <p className="text-sm text-gray-500 mt-2">{customerName}</p>
            )}
          </div>
          <div className="text-sm space-y-3">
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="font-bold text-black">Receipt Date</span>
              <span>{date}</span>
            </div>
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="font-bold text-black">Receipt Number</span>
              <span>{receiptNumber}</span>
            </div>
            <div className="grid grid-cols-[120px_1fr] gap-2">
              <span className="font-bold text-black">Reference</span>
              <span>{description ? 'Payment' : 'One-Time Payment'}</span>
            </div>
          </div>
        </div>

        {/* Line items */}
        <div className="mt-16">
          <div className="border-t border-gray-300" />
          <div className="flex justify-between py-2 text-sm font-bold text-black border-b border-gray-300">
            <span>Description</span>
            <span>Amount {currency}</span>
          </div>
          <div className="flex justify-between py-3 text-sm">
            <span className="pr-8">{description || 'Payment received'}</span>
            <span>{amount.toFixed(2)}</span>
          </div>

          {/* Totals */}
          <div className="flex justify-end mt-2">
            <div className="w-72 text-sm">
              <div className="border-t border-gray-300 pt-2 flex justify-between">
                <span>Subtotal</span>
                <span>{amount.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-1">
                <span>TOTAL NO VAT</span>
                <span>0.00</span>
              </div>
              <div className="border-t border-gray-300 mt-1 pt-2 flex justify-between font-bold text-base text-black">
                <span>TOTAL {currency}</span>
                <span>{amount.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Paid + payment method */}
        <div className="mt-10 text-sm">
          <p className="font-bold text-black">Paid: {date}</p>
          <p className="text-gray-700">Payment Method: Bank Transfer</p>
        </div>

        {/* Billed To */}
          {hasContact && (
            <div className="mt-8 text-sm">
              <p className="font-bold text-black mb-1">Billed To</p>
              <div className="space-y-0.5 text-gray-700">
                {customerName && <p>{customerName}</p>}
                {includeAddress && customerAddress?.street && <p>{customerAddress.street}</p>}
                {includeAddress && customerAddress?.city && <p>{customerAddress.city}</p>}
                {includeAddress && customerAddress?.postcode && <p>{customerAddress.postcode}</p>}
                {includeAddress && customerAddress?.country && <p>{customerAddress.country}</p>}
                <div className="h-2" />
                {customerPhone && <p>{customerPhone}</p>}
                {customerEmail && <p>{customerEmail}</p>}
              </div>
            </div>
          )}

        {/* Bank details */}
        {bankFields.length > 0 && (
          <div className="mt-8 text-sm">
            <p className="font-bold text-black mb-2">Payment Made To</p>
            <div className="space-y-1">
              {bankFields.map((f) => (
                <div key={f.label} className="grid grid-cols-[140px_1fr] gap-2">
                  <span className="text-gray-500">{f.label}</span>
                  <span className="text-gray-800">{f.value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-auto pt-10">
          {officeLine && (
            <>
              <div className="border-t border-gray-300" />
              <p className="text-center text-xs text-gray-500 mt-3">{officeLine}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReceiptPreview;
